import { useState } from 'react';
import { useFamilyStore } from '@/store/familyStore';
import { useAuthStore } from '@/store/authStore';
import { TreeControls } from './components/TreeControls';
import { TreeCanvas } from './components/TreeCanvas';
import { TreeNodeDialog } from './components/TreeNodeDialog';
import { EmptyState } from './components/EmptyState';
import { useTreeLayout } from './hooks/useTreeLayout';
import { useTreeNavigation } from './hooks/useTreeNavigation';
import { useTreeDimensions } from './hooks/useTreeDimensions';
import { TREE_CONFIG } from '@/config';
import type { FamilyMember } from '@/types';

interface DialogState {
  mode: 'edit' | 'add' | 'delete' | null;
  member: FamilyMember | null;
}

export function FamilyTree() {
  const { members, addMember, updateMember, deleteMember } = useFamilyStore();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const [dialog, setDialog] = useState<DialogState>({
    mode: null,
    member: null
  });

  // Get container dimensions
  const dimensions = useTreeDimensions();

  // Calculate tree layout
  const { nodePositions, contentDimensions } = useTreeLayout({
    members,
    nodeWidth: TREE_CONFIG.NODE_WIDTH,
    nodeHeight: TREE_CONFIG.NODE_HEIGHT,
    levelHeight: TREE_CONFIG.LEVEL_HEIGHT,
    frame: dimensions
  });

  // Handle navigation
  const {
    scale,
    position,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    handleButtonZoom
  } = useTreeNavigation({
    minScale: TREE_CONFIG.MIN_SCALE,
    maxScale: TREE_CONFIG.MAX_SCALE,
    containerDimensions: dimensions
  });

  const handleDialogConfirm = (names?: string[]) => {
    if (!names?.length) return;

    if (!dialog.member) {
      // Adding first member
      const newMember: FamilyMember = {
        id: Math.random().toString(36).substr(2, 9),
        name: names[0],
        children: []
      };
      addMember(newMember);
    } else {
      switch (dialog.mode) {
        case 'edit':
          updateMember(dialog.member.id, { name: names[0] });
          break;
        case 'add':
          // Create multiple children
          const newChildren = names.map(name => ({
            id: Math.random().toString(36).substr(2, 9),
            name,
            parentId: dialog.member!.id,
            children: []
          }));
          
          // Add all children
          newChildren.forEach(child => addMember(child));
          
          // Update parent's children array
          updateMember(dialog.member.id, {
            children: [...dialog.member.children, ...newChildren.map(c => c.id)]
          });
          break;
        case 'delete':
          deleteMember(dialog.member.id);
          break;
      }
    }

    setDialog({ mode: null, member: null });
  };

  const handleAddFirstMember = () => {
    setDialog({
      mode: 'add',
      member: null
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div 
        className="relative flex-1 bg-gray-50 overflow-hidden border border-border rounded-lg mt-2"
        style={{ minHeight: 'calc(100vh - 15rem)' }}
      >
        <div 
          className="absolute inset-0"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          {members.length === 0 ? (
            <EmptyState 
              isAdmin={isAdmin} 
              onAddFirstMember={isAdmin ? handleAddFirstMember : undefined}
            />
          ) : (
            <>
              <TreeControls
                onZoomIn={() => handleButtonZoom(true)}
                onZoomOut={() => handleButtonZoom(false)}
              />

              <TreeCanvas
                members={members}
                nodePositions={nodePositions}
                dimensions={dimensions}
                contentDimensions={contentDimensions}
                scale={scale}
                position={position}
                isDragging={isDragging}
                nodeWidth={TREE_CONFIG.NODE_WIDTH}
                nodeHeight={TREE_CONFIG.NODE_HEIGHT}
                onEditNode={(member) => setDialog({ mode: 'edit', member })}
                onDeleteNode={(member) => setDialog({ mode: 'delete', member })}
                onAddChild={(member) => setDialog({ mode: 'add', member })}
                isAdmin={isAdmin}
              />
            </>
          )}
        </div>
      </div>

      {(dialog.mode || (!dialog.member && dialog.mode === 'add')) && (
        <TreeNodeDialog
          mode={dialog.mode}
          member={dialog.member || { id: '', name: '', children: [] }}
          open={true}
          onClose={() => setDialog({ mode: null, member: null })}
          onConfirm={handleDialogConfirm}
        />
      )}
    </div>
  );
}